import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  BlogPost,
  StrapiResponse,
  Author,
  FeaturedPostsSection,
  TestimonialsSection,
} from "../models/blog-post";
import { BlogLanding } from "../models/blog-landing";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class BlogService {
  private apiUrl = environment.strapiUrl + "/api";

  constructor(private http: HttpClient) {}

  // Get blog landing page
  getBlogLanding(): Observable<StrapiResponse<BlogLanding>> {
    const params = new HttpParams().set("populate", "*");

    return this.http.get<StrapiResponse<BlogLanding>>(
      `${this.apiUrl}/blog-posts-landing-page`,
      { params },
    );
  }

  // Get all posts with pagination
  getPosts(
    page: number = 1,
    pageSize: number = 12,
  ): Observable<StrapiResponse<BlogPost[]>> {
    const params = new HttpParams()
      .set("pagination[page]", page.toString())
      .set("pagination[pageSize]", pageSize.toString())
      .set("populate", "*")
      .set("sort", "createdAt:desc");

    return this.http.get<StrapiResponse<BlogPost[]>>(
      `${this.apiUrl}/articles`,
      { params },
    );
  }

  // Get single post by slug
  getPostBySlug(slug: string): Observable<StrapiResponse<BlogPost>> {
    const params = new HttpParams()
      .set("filters[slug][$eq]", slug)
      .set("populate[cover]", "true")
      .set("populate[author]", "true")
      .set("populate[category]", "true")
      .set("populate[blocks][on][shared.media][populate][file]", "true")
      .set("populate[blocks][on][shared.quote]", "true")
      .set("populate[blocks][on][shared.rich-text]", "true");

    return this.http
      .get<StrapiResponse<BlogPost[]>>(`${this.apiUrl}/articles`, { params })
      .pipe(
        map((response) => ({
          data: response.data[0],
          meta: response.meta,
        })),
      );
  }

  // Get featured posts
  getFeaturedPosts(): Observable<StrapiResponse<FeaturedPostsSection>> {
    const params = new HttpParams().set(
      "populate[articles][populate][0]",
      "cover",
    );

    return this.http.get<StrapiResponse<FeaturedPostsSection>>(
      `${this.apiUrl}/featured-posts-section`,
      { params },
    );
  }

  // Get Testimonials
  getTestimonials(): Observable<StrapiResponse<TestimonialsSection>> {
    const params = new HttpParams().set(
      "populate[articles][populate][0]",
      "cover",
    );

    return this.http.get<StrapiResponse<TestimonialsSection>>(
      `${this.apiUrl}/testimonials-section`,
      { params },
    );
  }

  // Get image URL
  getImageUrl(url: string): string {
    return url.startsWith("http") ? url : `${environment.strapiUrl}${url}`;
  }

  // Get author by id
  getAuthorById(id: number): Observable<StrapiResponse<Author>> {
    const params = new HttpParams()
      .set("id[id]", id.toString())
      .set("populate", "*");

    return this.http.get<StrapiResponse<Author>>(`${this.apiUrl}/authors`, {
      params,
    });
  }

  getAuthorsByIds(ids: number[]): Observable<Author[]> {
    const params = new HttpParams()
      .set("filters[id][$in]", ids.join(","))
      .set("populate", "*");

    return this.http
      .get<StrapiResponse<Author[]>>(`${this.apiUrl}/authors`, { params })
      .pipe(map((response) => response.data));
  }

  // Get previous post (newer post)
  getPreviousPost(currentCreatedAt: string): Observable<BlogPost | null> {
    const params = new HttpParams()
      .set("filters[createdAt][$gt]", currentCreatedAt)
      .set("populate[cover]", "true")
      .set("populate[author]", "true")
      .set("populate[category]", "true")
      .set("sort", "createdAt:asc")
      .set("pagination[pageSize]", "1");

    return this.http
      .get<StrapiResponse<BlogPost[]>>(`${this.apiUrl}/articles`, { params })
      .pipe(
        map((response) => (response.data.length > 0 ? response.data[0] : null)),
      );
  }

  // Get next post (older post)
  getNextPost(currentCreatedAt: string): Observable<BlogPost | null> {
    const params = new HttpParams()
      .set("filters[createdAt][$lt]", currentCreatedAt)
      .set("populate[cover]", "true")
      .set("populate[author]", "true")
      .set("populate[category]", "true")
      .set("sort", "createdAt:desc")
      .set("pagination[pageSize]", "1");

    return this.http
      .get<StrapiResponse<BlogPost[]>>(`${this.apiUrl}/articles`, { params })
      .pipe(
        map((response) => (response.data.length > 0 ? response.data[0] : null)),
      );
  }
}
